/**
 * Analytics Plugin (client)
 * Initializes analytics providers and auto-tracking listeners.
 * v-track directives are registered in analytics-directives.ts (SSR + client).
 *
 * This file is a composition root (wiring only). It cannot be imported under the Nuxt Vitest
 * transform because it depends on the nuxt-gtag `useGtag` auto-import (`#imports`), which is not
 * registered in the test build. Its extractable logic is unit-tested elsewhere: the auth gate in
 * utils/auth-gate.spec.ts, the page-view dispatch in tracking/page-view-pipeline.spec.ts, the
 * backend event mapping in providers/backend-analytics.provider.spec.ts, and identify()/setContext
 * in services/analytics.service.spec.ts.
 */

import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { getConfig } from '@common/core-layer/config'
import { APP_NAMES } from '@common/core-layer/stores/global.store'
import { AnalyticsEvents } from '../types'
import { BackendAnalyticsProvider } from '../providers/backend-analytics.provider'
import { PendoAnalyticsProvider } from '../providers/pendo-analytics.provider'
import { analyticsService } from '../services/analytics.service'
import { registerDefaultIdentitySource } from '../tracking/analytics-context'
import {
  hasAuthenticatedAnalyticsIdentity,
  readAuthenticatedAnalyticsContactId,
  watchAuthenticatedAnalyticsIdentity,
} from '../utils/auth-gate'
import { createAutoClickTracking } from '../tracking/auto-click-tracking'
import { startPageViewTracking } from '../tracking/page-view-pipeline'
import { registerGoogleAnalyticsFromPlugin } from '../utils/register-google-analytics-from-plugin.utils'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import { registerGokollabCpStream, resetGokollabCpStream } from '../utils/register-gokollab-dual-stream.utils'
import { resolveAppFlavor } from '../utils/page-hit'
import { getGtag, setGtagUserId } from '../utils/gtag-access'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const analyticsConfig = config.public.analytics
  const { logger } = useLogger()

  if (!analyticsConfig?.enabled) {
    logger.info('[Analytics] Disabled')
    return
  }

  const appName = (config.public.appName as string) || ''
  const isGokollabApp = appName === APP_NAMES.GOKOLLAB

  // The current-user boundary exposes auth-session identity before the slower profile request
  // settles. Route contact remains a separate attribution requirement inside AnalyticsService.
  let getAuthenticatedContactId: ReturnType<typeof useCurrentUser>['getAuthenticatedContactId'] | undefined
  try {
    const currentUser = useCurrentUser()
    getAuthenticatedContactId = currentUser.getAuthenticatedContactId
  } catch (error) {
    logger.warn('[Analytics] Current user store unavailable', { error: error as Error })
  }

  const hasAuthenticatedIdentity = (): boolean => hasAuthenticatedAnalyticsIdentity(getAuthenticatedContactId?.())

  analyticsService.initialize(analyticsConfig.debug || false, logger, {
    isAuthenticated: hasAuthenticatedIdentity,
    // Page hits and controlled backend diagnostics are anonymous-capable everywhere. GA auto
    // events retain the existing GoKollab-only anonymous behavior.
    allowsAnonymous: provider => provider !== 'googleAnalytics' || isGokollabApp,
  })

  // Global (non-tenant) identity for the default resolver. Sourced from location + current-user
  // (never auth-layer) so the analytics layer does not import auth.store — that would cycle with
  // auth-layer, which already imports analytics types.
  const locationStore = useLocationStore()
  registerDefaultIdentitySource({
    getLocationId: () => locationStore.locationId,
    getContactId: () => readAuthenticatedAnalyticsContactId(getAuthenticatedContactId),
  })

  const cleanupFunctions: (() => void)[] = []

  // ===== Register providers =====

  registerGoogleAnalyticsFromPlugin(
    {
      ...analyticsConfig,
      appEnv: config.public.appEnv as string,
    },
    useGtag,
    logger
  )

  if (analyticsConfig.providers?.backendAnalytics?.enabled) {
    analyticsService.registerProvider('backendAnalytics', new BackendAnalyticsProvider(analyticsConfig.debug, logger))
  }

  if (analyticsConfig.providers?.pendoAnalytics?.enabled) {
    analyticsService.registerProvider('pendoAnalytics', new PendoAnalyticsProvider(resolveAppFlavor(config.public.appName)))
  }

  // ===== User identity lifecycle =====
  // Logout cleanup is independent of optional identification tracking so stale route identity can
  // never survive simply because autoTrack.userIdentification is disabled.
  const stopUserWatch = watchAuthenticatedAnalyticsIdentity(getAuthenticatedContactId, contactId => {
    if (!contactId) {
      analyticsService.clearIdentity()
      resetGokollabCpStream()
      try {
        setGtagUserId(getGtag().gtag, null)
      } catch (error) {
        logger.warn('[Analytics] GA User-ID cleanup unavailable', { error: error as Error })
      }
      return
    }

    if (analyticsConfig.autoTrack?.userIdentification === false) return

    try {
      const { gtag } = getGtag()
      const canSendToGa = isGoogleAnalyticsSendEnabled(config.public.appEnv as string)
      setGtagUserId(gtag, contactId)

      analyticsService.identify(
        contactId,
        {
          appName: config.public.appName || 'Unknown',
        },
        { kind: 'platform', locationId: locationStore.locationId }
      )

      if (canSendToGa && appName === APP_NAMES.GOKOLLAB) {
        registerGokollabCpStream(gtag, getConfig().GOOGLE_ANALYTICS.GOKOLLAB_CP_STREAM, canSendToGa, logger)
      }
    } catch (error) {
      logger.warn('[Analytics] User tracking not available', { error: error as Error })
    }
  })

  cleanupFunctions.push(stopUserWatch)

  // ===== Auto-track: Clicks (via element id convention) =====
  if (analyticsConfig.autoTrack?.clicks !== false) {
    // Keyboard activation of an ARIA control never produces a click of its own, so without the
    // keyboard path the same button reports for pointer users and reports nothing for keyboard and
    // screen-reader users. The two handlers share state to stay one count per activation — see
    // tracking/auto-click-tracking.ts.
    const { handleClick, handleKeyboardActivation } = createAutoClickTracking((event, properties) =>
      analyticsService.track(event, properties)
    )

    document.addEventListener('click', handleClick, { capture: true, passive: true })
    document.addEventListener('keydown', handleKeyboardActivation, { capture: true, passive: true })
    cleanupFunctions.push(() => document.removeEventListener('click', handleClick, { capture: true }))
    cleanupFunctions.push(() => document.removeEventListener('keydown', handleKeyboardActivation, { capture: true }))
  }

  // ===== Auto-track: Page Views & Route Context =====
  // The pipeline resolves and commits route context, then emits one page event. The service applies
  // destination access policy and fans the trusted envelope out to registered providers.
  if (analyticsConfig.autoTrack?.pageViews !== false) {
    cleanupFunctions.push(startPageViewTracking())
  }

  // ===== Auto-track: Errors =====
  if (analyticsConfig.autoTrack?.errors !== false) {
    const handleError = (event: ErrorEvent) => {
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: event.message,
        errorType: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: (event.reason as Error)?.message || String(event.reason),
        errorType: 'unhandled_promise_rejection',
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    cleanupFunctions.push(() => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    })
  }

  // ===== Cleanup =====
  let isCleanedUp = false

  const performCleanup = () => {
    if (isCleanedUp) return
    cleanupFunctions.forEach(fn => fn())
    isCleanedUp = true
  }

  // beforeunload paired with its removeEventListener in cleanup
  window.addEventListener('beforeunload', performCleanup)
  cleanupFunctions.push(() => window.removeEventListener('beforeunload', performCleanup))

  logger.info('[Analytics] Initialized successfully')
})
